import { useTranslation } from "react-i18next"

import type { ClaimVerificationStatus, SourceEvidence } from "../types"

import { AlertTriangle, CheckCircle2, HelpCircle, XCircle } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { SnippetCard } from "./snippet-card"

type EvidenceCellProps = {
  evidence: SourceEvidence
  className?: string
}

const CONFIDENCE_DOT: Record<string, string> = {
  saker: "bg-success",
  osaker: "bg-warning",
  ej_verifierbar: "bg-destructive",
}

const VERIFICATION_DOT: Record<string, string> = {
  verified: "bg-success",
  partially_verified: "bg-warning",
  unverified: "bg-muted-foreground",
  contradicted: "bg-destructive",
}

const VERIFICATION_ROW_STYLE: Record<
  ClaimVerificationStatus,
  { icon: React.ElementType; className: string }
> = {
  verified: {
    icon: CheckCircle2,
    className: "text-success",
  },
  partially_verified: {
    icon: HelpCircle,
    className: "text-warning",
  },
  unverified: {
    icon: AlertTriangle,
    className: "text-muted-foreground",
  },
  contradicted: {
    icon: XCircle,
    className: "text-destructive",
  },
}

// Inline badge that shows source count + confidence/verification dot.
// Click to open a floating popover with full reasoning and snippets.
export function EvidenceCell({ evidence, className }: EvidenceCellProps) {
  const { t } = useTranslation()
  const dotColor = evidence.verification
    ? VERIFICATION_DOT[evidence.verification.status]
    : CONFIDENCE_DOT[evidence.confidence]

  const count = evidence.snippets.length
  const label = t("tender.evidence.sourceCount", { count })

  const verification = evidence.verification
  const verConfig = verification ? VERIFICATION_ROW_STYLE[verification.status] : null
  const VerIcon = verConfig?.icon
  const verificationLabel = verification
    ? t(`tender.verification.${verification.status}.label`)
    : null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "h-auto gap-1.5 rounded border px-2 py-1 text-xs font-medium",
            "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
            "data-[state=open]:bg-accent data-[state=open]:text-foreground",
            className
          )}
        >
          <span className={cn("size-2 shrink-0 rounded-full", dotColor)} />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="border-border/60 bg-background/80 w-[360px] space-y-3 border p-4 shadow-xl backdrop-blur-md"
        sideOffset={6}
      >
        {/* Reasoning */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-muted-foreground text-xs font-medium">
              {t("tender.bidReview.popover.reasoning")}
            </p>
            {verConfig && VerIcon && verificationLabel && (
              <div
                className={cn(
                  "flex shrink-0 items-center gap-1 text-xs font-medium",
                  verConfig.className
                )}
              >
                <VerIcon className="size-3 shrink-0" />
                {verificationLabel}
                {verification?.checkedBy === "human" && (
                  <span className="text-muted-foreground font-normal">
                    {t("tender.verification.manualSuffix")}
                  </span>
                )}
              </div>
            )}
          </div>
          <p className="text-sm leading-relaxed">{evidence.reasoning}</p>
        </div>

        {/* Source snippets */}
        {evidence.snippets.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium">
              {evidence.snippets.length === 1
                ? t("tender.bidReview.popover.source")
                : t("tender.bidReview.popover.sources")}
            </p>
            <div className="divide-border/30 divide-y">
              {evidence.snippets.map((snippet, i) => (
                <SnippetCard key={i} snippet={snippet} />
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
